/**
 * API client for EU Risk University backend
 */

import type { ApiResponse, Institution, RiskScore, InstitutionSummary, BlastRadiusResult, OwnershipChain, OverviewStats, SearchParams } from '../types';

// Use same-origin API calls
// In production: Pages Function proxies to Workers
// In development: Vite proxy forwards to local Workers
const API_BASE = '/api/v1';

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include', // Send Access cookies cross-origin
    ...options,
  });

  const json = await response.json() as ApiResponse<T>;

  if (!json.success || !response.ok) {
    throw new Error(json.error?.message || 'API request failed');
  }

  return json.data as T;
}

// Institutions
export async function getInstitution(lei: string): Promise<Institution> {
  return fetchApi<Institution>(`/institutions/${lei}`);
}

export async function getInstitutionSummary(lei: string): Promise<InstitutionSummary> {
  return fetchApi<InstitutionSummary>(`/institutions/${lei}/risk-summary`);
}

export async function searchInstitutions(params: SearchParams): Promise<{ institutions: Institution[]; total: number }> {
  const queryParams = new URLSearchParams();
  if (params.q) queryParams.set('q', params.q);
  if (params.jurisdiction) queryParams.set('jurisdiction', params.jurisdiction);
  if (params.institution_type) queryParams.set('type', params.institution_type);
  if (params.status) queryParams.set('status', params.status);
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());

  const response = await fetch(`${API_BASE}/search/institutions?${queryParams}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  const json = await response.json() as ApiResponse<Institution[]> & { meta?: { total: number } };

  if (!json.success) {
    throw new Error(json.error?.message || 'Search failed');
  }

  return {
    institutions: json.data || [],
    total: json.meta?.total || 0,
  };
}

// Risk Scores
export async function getRiskScore(lei: string): Promise<RiskScore> {
  return fetchApi<RiskScore>(`/risk-scores/${lei}`);
}

export async function getTopRisk(limit = 20): Promise<Array<{ lei: string; legal_name: string; overall_score: number; overall_grade: string }>> {
  return fetchApi(`/risk-scores/top-risk?limit=${limit}`);
}

// Blast Radius
export async function getBlastRadius(lei: string, depth = 5): Promise<BlastRadiusResult> {
  return fetchApi<BlastRadiusResult>(`/blast-radius/${lei}?depth=${depth}`);
}

// Ownership Chain
export async function getOwnershipChain(lei: string): Promise<OwnershipChain> {
  return fetchApi<OwnershipChain>(`/ownership-chain/${lei}`);
}

// Statistics
export async function getOverviewStats(): Promise<OverviewStats> {
  return fetchApi<OverviewStats>('/stats/overview');
}

export async function getEnforcementStats(): Promise<Record<string, unknown>> {
  return fetchApi('/stats/enforcement');
}

// Operations Dashboard
export interface OpsStatus {
  timestamp: string;
  overall_health: 'healthy' | 'degraded' | 'error';
  data_sources: Array<{
    source_code: string;
    source_name: string;
    source_type: string;
    is_active: boolean;
    expected_update_frequency: string;
    last_status: string | null;
    last_run_at: string | null;
    last_completed_at: string | null;
    duration_seconds: number | null;
    last_records_processed: number | null;
    errors: string[] | null;
    health_status: string | null;
  }>;
  recent_runs: Array<{
    job_name: string;
    status: string;
    started_at: string;
    completed_at: string | null;
    duration_seconds: number | null;
    records_processed: number;
    errors: string[] | null;
    health_status: string;
  }>;
  data_coverage: {
    institutions: number;
    risk_scores: number;
    enforcement_records: number;
    privacy_policies: number;
    corporate_relationships: number;
  };
}

export async function getOpsStatus(): Promise<OpsStatus> {
  return fetchApi<OpsStatus>('/ops/status');
}
